import Client from "..";
import { BaseItem, ItemType, SlotType } from "../inventory/BaseItem";
import { DropZoneShape, ItemDropZone } from "../utils/ItemDropZone";
import { GameEvents } from "../utils/Types";
import { ItemDescriptor } from "./windows/ItemDescriptor";
import { NineSlicePresets } from "./NineSlicePresets";
import { Phase } from "../utils/StateMachine";

class InventorySlot extends Phaser.GameObjects.Container {
  item: BaseItem | null = null;
  image?: Phaser.GameObjects.Image;
  background: Phaser.GameObjects.NineSlice;

  imageInitialScale: number = 1;
  private imageInitialPosition: Phaser.Math.Vector2 = new Phaser.Math.Vector2(
    0,
    0,
  );
  private dragOffset: Phaser.Math.Vector2 = new Phaser.Math.Vector2(0, 0);

  private pointerDown = false;
  private moveTween?: Phaser.Tweens.Tween;
  private itemDescriptor?: ItemDescriptor;

  private dropZone: ItemDropZone<SlotType>;

  constructor(
    scene: Phaser.Scene,
    itemDescriptor?: ItemDescriptor,
    item?: BaseItem,
  ) {
    super(scene);

    this.background = NineSlicePresets.Default(scene).setTint(0x9c7a4d);
    this.add(this.background);

    this.dropZone = new ItemDropZone<SlotType>(
      scene,
      [SlotType.INVENTORY],
      this.onZoneEnter.bind(this),
      this.onZoneLeave.bind(this),
      this.onZoneDrop.bind(this),
    );
    this.add(this.dropZone);

    this.itemDescriptor = itemDescriptor;
    if (item) this.FillItem(item);
  }

  public Resize(
    width: number = this.width,
    height: number = this.height,
  ): void {
    this.setSize(width, height);

    this.dropZone.SetShape(DropZoneShape.RECTANGLE, width, height);

    this.background.setSize(width, height).setDisplaySize(width, height);
    this.background.setPosition(
      this.background.displayWidth * 0.5,
      this.background.displayHeight * 0.5,
    );

    if (this.image) {
      this.image.setDisplaySize(width * 0.7, height * 0.7);
      this.image.setScale(Math.min(this.image.scaleX, this.image.scaleY));
      this.imageInitialScale = this.image.scale;
      this.imageInitialPosition.set(this.width * 0.5, this.height * 0.5);
      this.image.setPosition(
        this.imageInitialPosition.x,
        this.imageInitialPosition.y,
      );
    }
  }

  public FillItem(item: BaseItem): void {
    this.item = item;
    if (!this.image) {
      this.image = this.scene.add.image(0, 0, "items").setOrigin(0.5);
      this.add(this.image);

      this.scene.input.on(Phaser.Input.Events.DRAG, this.onDrag, this);
      this.image.on(Phaser.Input.Events.DRAG_START, this.onDragStart, this);
      this.image.on(Phaser.Input.Events.DRAG_END, this.onDragEnd, this);
      this.image.on(Phaser.Input.Events.DROP, this.onDragDrop, this);
      this.image.on(Phaser.Input.Events.DRAG_ENTER, this.onDragEnter, this);
      this.image.on(Phaser.Input.Events.DRAG_LEAVE, this.onDragLeave, this);
      this.image.on(Phaser.Input.Events.POINTER_OVER, this.onHoverStart, this);
      this.image.on(Phaser.Input.Events.POINTER_OUT, this.onHoverEnd, this);
      this.image.setInteractive();
    }

    this.image.setTexture(item.textureKey, item.frameKey);

    // Force a size update - use container dimensions if available
    if (this.width > 0 && this.height > 0) {
      this.Resize();
    } else {
      // Fallback: set a reasonable default size
      const size = 64; // Default item size
      this.image.setDisplaySize(size * 0.7, size * 0.7);
      this.image.setPosition(size * 0.5, size * 0.5);
    }

    this.image.setVisible(true);
    this.scene.input.setDraggable(this.image);

    this.image.setScale(0);
    this.scene.tweens.add({
      targets: this.image,
      duration: 150,
      scale: this.imageInitialScale,
      ease: "Power2",
    });
  }

  public ClearItem(): void {
    this.item = null;
    if (this.image) {
      this.scene.input.setDraggable(this.image, false);
      const imageCopy = new Phaser.GameObjects.Image(
        this.scene,
        this.image.x,
        this.image.y,
        this.image.texture,
        this.image.frame.name,
      );
      imageCopy.setScale(this.image.scale);
      this.add(imageCopy);

      this.image?.setTexture("", "");
      this.image?.setVisible(false);

      this.scene.tweens.add({
        targets: imageCopy,
        duration: 150,
        scale: 0,
        ease: "Power2",
        onComplete: () => {
          imageCopy.destroy();
        },
      });
    }
  }

  private onDragStart(pointer: Phaser.Input.Pointer): void {
    this.pointerDown = true;
    if (!this.item || !this.image) return;

    // Hide descriptor when dragging starts
    if (this.itemDescriptor) {
      this.itemDescriptor.hideDescriptor();
    }

    this.moveTween?.stop();
    this.parentContainer.bringToTop(this);

    const worldPoint = this.image.getWorldPoint();
    const mousePoint = pointer.position;

    this.dragOffset.set(
      mousePoint.x - worldPoint.x,
      mousePoint.y - worldPoint.y,
    );

    this.scene.tweens.add({
      targets: this.image,
      scale: this.imageInitialScale * 1.3,
      x: this.dragOffset.x + this.imageInitialPosition.x,
      y: this.dragOffset.y + this.imageInitialPosition.y,
      duration: 50,
      ease: "Power2",
    });

    if (Client.getActiveScene()?.shopVisible) {
      Client.game.events.emit(GameEvents.ENABLE_DROP_ZONES, [
        SlotType.STORE,
        SlotType.INVENTORY,
      ]);
    } else {
      Client.game.events.emit(GameEvents.ENABLE_DROP_ZONES, [
        this.item.itemType,
        SlotType.INVENTORY,
      ]);
    }
  }

  private onDragEnd(): void {
    this.pointerDown = false;

    if (!this.image) return;

    this.moveTween = this.scene.tweens.add({
      targets: this.image,
      scale: this.imageInitialScale,
      x: this.imageInitialPosition.x,
      y: this.imageInitialPosition.y,
      duration: 150,
      ease: "Power2",
    });
    Client.game.events.emit(GameEvents.DISABLE_DROP_ZONES);
  }

  private onDrag(
    _pointer: Phaser.Input.Pointer,
    _gameObject: Phaser.GameObjects.GameObject,
    dragX: number,
    dragY: number,
  ): void {
    if (!this.pointerDown || !this.image || !this.item) return;

    this.image.setPosition(
      dragX + this.dragOffset.x,
      dragY + this.dragOffset.y,
    );
  }

  private onDragDrop(
    _pointer: Phaser.Input.Pointer,
    dropZoneElement: ItemDropZone<ItemType | SlotType>,
  ): void {
    Client.game.events.emit(GameEvents.DISABLE_DROP_ZONES);

    // Check if item exists before proceeding
    if (!this.item) {
      return;
    }

    if (Client.state.wheelSpinning && Client.state.phase !== Phase.STORE) {
      return;
    }

    this.dragOffset.set(0, 0);

    // Store item reference before it potentially gets cleared
    const currentItem = this.item;
    console.log(
      "Attempting to drop item:",
      currentItem.uniqueId,
      "Type:",
      currentItem.itemType,
    );

    // Check if this is a sell operation (STORE slot type)
    if (dropZoneElement.Supports(SlotType.STORE)) {
      const ok = dropZoneElement.OnZoneDrop(currentItem.effects);
      if (ok) {
        // Sell the item: add money and remove from inventory
        const itemValue = currentItem.value || 0;
        const itemId = currentItem.uniqueId!;

        Client.state.money += itemValue;

        // Remove from both item and charm inventories
        if (currentItem.itemType === ItemType.CHARM) {
          Client.state.charmInventory.RemoveItemFromInventory(itemId);
        } else {
          Client.state.itemInventory.RemoveItemFromInventory(itemId);
        }

        console.log(`Sold ${itemId} for $${itemValue}`);
      }
    } else if (currentItem.effects) {
      // Regular item usage (not selling)
      const ok = dropZoneElement.OnZoneDrop(currentItem.effects);
      if (ok) {
        // Remove from appropriate inventory based on item type
        if (currentItem.itemType === ItemType.CHARM) {
          Client.state.charmInventory.RemoveItemFromInventory(
            currentItem.uniqueId!,
          );
        } else {
          Client.state.itemInventory.RemoveItemFromInventory(
            currentItem.uniqueId!,
          );
        }
      }
    }
  }

  private onDragEnter(
    _pointer: Phaser.Input.Pointer,
    dropZoneElement: ItemDropZone<ItemType | SlotType>,
  ): void {
    if (!this.item || Client.state.wheelSpinning) return;

    dropZoneElement.OnZoneEnter(this.item.effects);
  }

  private onDragLeave(
    _pointer: Phaser.Input.Pointer,
    dropZoneElement: ItemDropZone<ItemType | SlotType>,
  ): void {
    if (!this.item || Client.state.wheelSpinning) return;

    dropZoneElement.OnZoneLeave(this.item.effects);
  }

  /**
   * Handle mouse hover start
   */
  private onHoverStart(): void {
    // Only show descriptor if item is not being dragged and is in its socket
    if (this.item && this.itemDescriptor && !this.pointerDown) {
      const pointer = this.scene.input.activePointer;
      this.itemDescriptor.showForItem(this.item, pointer.x, pointer.y);
    }
  }

  /**
   * Handle mouse hover end
   */
  private onHoverEnd(): void {
    if (this.itemDescriptor) {
      this.itemDescriptor.hideDescriptor();
    }
  }

  private onZoneEnter(): void {
    console.log("Item entered inventory zone");
  }

  private onZoneLeave(): void {
    console.log("Item left inventory zone");
  }

  private onZoneDrop(): boolean {
    console.log("Item dropped in inventory zone");
    return false;
  }
}

export class Inventory extends Phaser.GameObjects.Container {
  inventorySlots: InventorySlot[] = [];
  private itemDescriptor: ItemDescriptor;

  debugGraphics: Phaser.GameObjects.Graphics;
  constructor(
    scene: Phaser.Scene,
    numberOfInventorySlots: number,
    private readonly vertical: boolean,
  ) {
    super(scene);

    // Create item descriptor
    this.itemDescriptor = new ItemDescriptor(scene);
    scene.add.existing(this.itemDescriptor);

    for (let i = 0; i < numberOfInventorySlots; i++) {
      const slot = new InventorySlot(scene, this.itemDescriptor);
      this.inventorySlots.push(slot);
      this.add(slot);
    }

    this.debugGraphics = new Phaser.GameObjects.Graphics(scene);
    this.add(this.debugGraphics);
  }

  public Resize(width: number, height: number): void {
    if (this.vertical) {
      const slotSize = Math.min(height / this.inventorySlots.length, width);
      const slotSizeGap = slotSize * 0.1;
      this.inventorySlots.forEach((slot, index) => {
        slot.Resize(slotSize, slotSize);
        slot.setPosition(0, index * (slotSize + slotSizeGap));
      });
      this.setSize(
        slotSize,
        slotSize * this.inventorySlots.length +
          slotSizeGap * (this.inventorySlots.length - 1),
      );
    } else {
      const slotSize = Math.min(height, width / this.inventorySlots.length);
      const slotSizeGap = slotSize * 0.1;
      this.inventorySlots.forEach((slot, index) => {
        slot.Resize(slotSize, slotSize);
        slot.setPosition(index * (slotSize + slotSizeGap), 0);
      });

      this.setSize(
        slotSize * this.inventorySlots.length +
          slotSizeGap * (this.inventorySlots.length - 1),
        slotSize,
      );
    }
  }

  public GetItem(uniqueId: string | undefined): InventorySlot | null {
    if (!uniqueId) return null;

    for (let i = 0; i < this.inventorySlots.length; i++) {
      if (this.inventorySlots[i].item?.uniqueId === uniqueId)
        return this.inventorySlots[i];
    }
    return null;
  }

  public LoadState(itemInventory: (BaseItem | null)[]): void {
    for (let i = 0; i < itemInventory.length; i++) {
      if (i >= this.inventorySlots.length) break;
      if (itemInventory[i] !== null) {
        if (itemInventory[i] !== this.inventorySlots[i].item)
          this.inventorySlots[i].FillItem(itemInventory[i] as BaseItem);
      } else {
        this.inventorySlots[i].ClearItem();
      }
    }
  }

  /**
   * Disable all inventory interactions (dragging, hover, etc.)
   */
  public disableAllInteractions(): void {
    for (const slot of this.inventorySlots) {
      if (slot.image) {
        // Disable dragging
        slot.scene.input.setDraggable(slot.image, false);
        // Disable all input interactions
        slot.image.disableInteractive();
      }
    }
  }

  /**
   * Re-enable all inventory interactions (dragging, hover, etc.)
   */
  public enableAllInteractions(): void {
    for (const slot of this.inventorySlots) {
      if (slot.image && slot.item) {
        // Re-enable dragging for items that exist
        slot.scene.input.setDraggable(slot.image, true);
        // Re-enable input interactions
        slot.image.setInteractive();
      }
    }
  }

  public override destroy(): void {
    if (this.itemDescriptor) {
      this.itemDescriptor.destroy();
    }
    super.destroy();
  }
}
